import { useCallback, useEffect, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';

import {
  AnswerSource,
  DocumentIndexStatus,
  createConversation,
  ensureDocumentIndexed,
  getDocumentStatusQuery,
  streamAnswer
} from 'Services';

import { AskPdfProps, ConversationProps } from '..';

/**
 * Drives the PDF assistant against the retrieval service.
 *
 * Questions only: answers stream over SSE and arrive with page citations.
 * The Overview and FAQ tabs are not rendered, so /insights is not called —
 * see the note on `questions` below.
 */
export const useAskPdf = ({ id }: Partial<AskPdfProps>) => {
  const [conversation, setConversation] = useState<ConversationProps[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const conversationId = useRef<string>('');
  const abortController = useRef<AbortController>();

  const { data: status, isLoading: isStatusLoading } = useQuery({
    ...getDocumentStatusQuery(id),
    enabled: !!id
  });

  // A document nobody has asked about yet may not be indexed. Asking for
  // it once moves it to the front of the queue; the status query polls.
  useEffect(() => {
    if (id && status?.status === DocumentIndexStatus.Pending)
      ensureDocumentIndexed(id).catch(() => {});
  }, [id, status?.status]);

  // Abandon an in-flight answer if the dialog closes mid-stream.
  useEffect(() => () => abortController.current?.abort(), []);

  const patchLastAnswer = useCallback(
    (patch: (entry: ConversationProps) => ConversationProps) =>
      setConversation(prev =>
        prev.map((entry, index) =>
          index === prev.length - 1 ? patch(entry) : entry
        )
      ),
    []
  );

  const getConversationId = useCallback(async () => {
    if (conversationId.current) return conversationId.current;

    const { conversationId: created } = await createConversation(id);
    conversationId.current = created;
    return created;
  }, [id]);

  const onSubmit = useCallback(
    async (question: string) => {
      if (!question?.trim() || loading) return;

      setError('');
      setLoading(true);
      setConversation(prev => [...prev, { question, answer: '', sources: [] }]);

      abortController.current?.abort();
      abortController.current = new AbortController();

      try {
        const activeConversationId = await getConversationId();

        await streamAnswer({
          conversationId: activeConversationId,
          question,
          signal: abortController.current.signal,
          onEvent: event => {
            switch (event.type) {
              case 'sources':
                patchLastAnswer(entry => ({
                  ...entry,
                  sources: event.sources
                }));
                break;
              case 'token':
                patchLastAnswer(entry => ({
                  ...entry,
                  answer: entry.answer + event.text
                }));
                break;
              case 'error':
                setError(event.message);
                setLoading(false);
                break;
              case 'done':
                setLoading(false);
                break;
              default:
                break;
            }
          }
        });
      } catch (err) {
        // An abort is a user action, not a failure worth surfacing.
        if ((err as Error)?.name !== 'AbortError')
          setError('Something went wrong. Please try again.');
      } finally {
        setLoading(false);
      }
    },
    [getConversationId, loading, patchLastAnswer]
  );

  return {
    error,
    loading,
    status: status?.status,
    pageCount: status?.pageCount,
    conversation,

    // Suggested-question chips are not shown for now, so nothing here calls
    // /insights. The endpoint, its React Query hook and its types all still
    // exist — re-enabling the chips (or the Overview/FAQ tabs) means restoring
    // getDocumentInsightsQuery here and returning its values, not rebuilding
    // anything.
    questions: [] as string[],

    initLoading: isStatusLoading,

    onSubmit
  };
};

export type { AnswerSource };
